import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router';
import { ArrowLeft, Mail, WifiOff } from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { DEFAULT_DEVICE_SETTINGS } from '@shripos/shared/constants';
import { Alert, EmptyState, PageLoader, PinPad, cn } from '@shripos/ui';
import { LanguagePicker } from '../../components/LanguagePicker.jsx';
import { api } from '../../lib/api.js';
import { kvGet } from '../../lib/offline/db.js';
import {
  markOfflineSession,
  offlineStaff,
  refreshOfflineStaff,
  rememberForReconnect,
  rememberPinSignIn,
  verifyOfflinePin,
} from '../../lib/offline/staff.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { useSession } from '../../stores/session.js';

/** Offline settings from the last menu copy on this device (the PIN screen has no other source offline). */
async function offlineSettings(me) {
  const catalog = me?.outlet?.id ? await kvGet(`catalog:${me.outlet.id}`) : null;
  return { ...DEFAULT_DEVICE_SETTINGS.offline, ...(catalog?.settings?.offline ?? {}) };
}

/**
 * Registered devices: staff tap their name, then enter their PIN. Also used for fast user
 * switching (the previous session on this device is ended by the server). Without internet,
 * people who signed in here recently switch in with a PIN checked on this device
 * (lib/offline/staff.js; Settings → Offline & displays).
 */
export function PinLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const device = useSession((s) => s.device);
  const me = useSession((s) => s.me);
  const notice = useSession((s) => s.notice);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const staff = useQuery({
    queryKey: ['device-staff'],
    queryFn: async () => {
      try {
        const r = await api.get('/devices/current/staff');
        await refreshOfflineStaff(r.items);
        return { items: r.items, offline: false };
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        useOffline.getState().set({ reachable: false });
        const settings = await offlineSettings(me ?? (await kvGet('me')));
        const items = settings.allowUserSwitching ? await offlineStaff(settings.userSwitchDays) : [];
        return { items, offline: true, settings };
      }
    },
    enabled: Boolean(device),
    networkMode: 'always',
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  if (!device) return <Navigate to="/login" replace />;
  if (staff.isLoading) return <PageLoader />;
  const offline = Boolean(staff.data?.offline);

  const submit = async (pin) => {
    setBusy(true);
    setError(null);
    const offlineSignIn = async () => {
      const settings = staff.data.settings ?? (await offlineSettings(me ?? (await kvGet('me'))));
      if (!settings.allowUserSwitching)
        throw new Error(t('pin.offlineOff', 'No internet. Switching users needs a connection on this device.'));
      const profile = await verifyOfflinePin(selected.id, pin, settings.userSwitchDays);
      rememberForReconnect(selected.id, pin);
      await markOfflineSession(true);
      useSession.getState().signedInOffline(profile);
    };
    try {
      if (offline) await offlineSignIn();
      else {
        try {
          const res = await api.post('/auth/pin-login', { userId: selected.id, pin });
          await markOfflineSession(false);
          useSession.getState().signedIn(res.me);
          void rememberPinSignIn(res.me, pin); // for switching in while offline later
        } catch (err) {
          if (!isNetworkError(err)) throw err;
          useOffline.getState().set({ reachable: false });
          await offlineSignIn(); // the connection dropped since the list loaded
        }
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.code === 'INVALID_CREDENTIALS' || err.wrongPin ? t('pin.wrong') : err.message);
      setAttempt((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  const items = staff.data?.items ?? [];
  return (
    <div className="flex min-h-full flex-col bg-gradient-to-b from-brand-50 to-stone-100">
      <header className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2.5">
          <img src="/favicon.svg" alt="" className="size-9" />
          <div className="leading-tight">
            <p className="font-semibold">{device.tenant?.name ?? BRAND.productName}</p>
            <p className="text-xs text-slate-500">
              {device.outlet?.name} · {device.device.name} ({device.device.code})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <LanguagePicker />
          {me ? (
            <Link to="/" className="text-sm text-slate-600 hover:text-brand-700">
              {t('common.cancel')}
            </Link>
          ) : (
            !offline && (
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700"
              >
                <Mail className="size-4" /> {t('pin.emailLogin')}
              </Link>
            )
          )}
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-10">
        {!selected ? (
          <>
            <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              {offline && <WifiOff className="size-6 text-amber-600" data-testid="pin-offline" aria-label="Offline" />}
              {t('pin.title')}
            </h1>
            <p className="mt-1 mb-8 text-slate-500">{t('pin.subtitle')}</p>
            {notice === 'offlineEnded' && (
              <Alert tone="info" className="mb-6 max-w-xl">
                {t('pin.backOnline', 'The internet is back. Enter your PIN to carry on.')}
              </Alert>
            )}
            {offline && (
              <Alert tone="warning" className="mb-6 max-w-xl">
                {staff.data.settings.allowUserSwitching
                  ? t(
                      'pin.offlineHint',
                      'No internet. Staff who signed in on this device with their PIN in the last {{days}} days can switch in.',
                      { days: staff.data.settings.userSwitchDays },
                    )
                  : t('pin.offlineOff', 'No internet. Switching users needs a connection on this device.')}
              </Alert>
            )}
            {staff.isError ? (
              <Alert tone="danger">{staff.error.message}</Alert>
            ) : items.length === 0 ? (
              <EmptyState
                title={offline ? t('pin.offlineNobody', 'Nobody can switch in offline here.') : t('pin.noStaff')}
              />
            ) : (
              <div className="grid w-full max-w-3xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {items.map((u) => {
                  const disabled = offline && !u.canSwitch;
                  return (
                    <button
                      key={u.id}
                      disabled={disabled}
                      onClick={() => {
                        setSelected(u);
                        setError(null);
                      }}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-2xl bg-white p-5 shadow-xs ring-1 ring-slate-200 transition',
                        disabled ? 'cursor-not-allowed opacity-50' : 'hover:ring-brand-400 active:scale-[0.98]',
                      )}
                    >
                      <span className="flex size-14 items-center justify-center rounded-full bg-brand-100 text-xl font-bold text-brand-800">
                        {u.name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="text-center font-medium text-slate-900">{u.name}</span>
                      <span className="text-xs text-slate-500">
                        {disabled ? t('pin.needsInternet', 'Needs internet') : u.roleName}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="w-full max-w-sm">
            <button
              onClick={() => setSelected(null)}
              className="mb-6 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="size-4" /> {t('common.back')}
            </button>
            <h1 className="mb-6 text-center text-xl font-semibold">{t('pin.enterPin', { name: selected.name })}</h1>
            {error && error !== t('pin.wrong') && (
              <Alert tone="danger" className="mb-4">
                {error}
              </Alert>
            )}
            <PinPad key={attempt} onSubmit={submit} disabled={busy} error={error === t('pin.wrong') ? error : null} />
            {offline && busy && (
              <p className="mt-3 text-center text-sm text-slate-500">{t('pin.checking', 'Checking…')}</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
